import { DEFAULT_FONT_WEIGHT, formatVariantLabel, parseFontVariant } from '@air/utils-fonts';

const loadedFonts = new Map<string, Promise<void>>();

export type LoadedFontEntry = {
  family: string;
  url: string;
  weight: string | undefined;
  style: string | undefined;
};

const fontRegistry = new Map<string, LoadedFontEntry>();

export function getLoadedFonts(): LoadedFontEntry[] {
  return [...fontRegistry.values()];
}

const fontKeyToFamily = new Map<string, string>();

export function registerFontKey(key: string, family: string) {
  fontKeyToFamily.set(key, family);
}

export function getFontFamily(key: string): string {
  return fontKeyToFamily.get(key) ?? key;
}

/**
 * Loads a font file into the browser via the FontFace API.
 * Caches promises to avoid duplicate loads.
 */
export async function loadFontFace(
  family: string,
  url: string,
  descriptors?: Pick<FontFaceDescriptors, 'weight' | 'style'>,
): Promise<void> {
  const cacheKey = `${family}:${url}`;
  const existing = loadedFonts.get(cacheKey);

  if (existing) {
    return existing;
  }

  const promise = (async () => {
    const face = new FontFace(family, `url(${url})`, { display: 'swap', ...descriptors });
    const loaded = await face.load();
    document.fonts.add(loaded);
  })().catch((error) => {
    loadedFonts.delete(cacheKey);
    fontRegistry.delete(cacheKey);
    throw error;
  });

  loadedFonts.set(cacheKey, promise);
  fontRegistry.set(cacheKey, { family, style: descriptors?.style, url, weight: descriptors?.weight });

  return promise;
}

export { DEFAULT_FONT_WEIGHT, formatVariantLabel, parseFontVariant };
